import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, CardHeader, InputGroup, Input, InputGroupText } from "reactstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import ButtonComponent from "../../../../components/ButtonComponent";
import {
  useDeleteSetMenuMutation,
} from "../../../../redux/services/setMenuService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faTrash, faSpinner, faSearch } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { SetMenu, setRowsPerPage } from "../../../../redux/setMenuSlice";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import { Button } from "react-bootstrap";
import "./index.css";
import { useDispatch, useSelector } from "react-redux";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import { useUpdateAvailabilityStatusMutation } from "../../../../redux/services/menuItemService";
import { toast } from "react-toastify";
import { formatFirestoreTimestamp, getImageUrlWithFallback } from "../../../../utility/Utility";
import { DEFAULT_IMAGE_URL } from "../../../../constants/constans";
import TableLoadingComponent from "../../../../components/TableLoadingComponent";
import { useFirebaseSetMenuList } from "../../../../hooks/useFirebaseMenu";

export const useFetchSetMenus = (restaurantId: string) => {
  const [setMenus, setSetMenus] = useState<SetMenu[]>([]);
  const { data, loading: isLoading } = useFirebaseSetMenuList(restaurantId);

  useEffect(() => {
    if (data?.setMenus) {
      setSetMenus(data.setMenus);
    }
  }, [data]);

  return { setMenus, isLoading };
};

const useSetMenuMutations = (
  restaurantId: string
) => {
  const [deleteSetMenu] = useDeleteSetMenuMutation();
  const [updateAvailabilityStatus] = useUpdateAvailabilityStatusMutation();
  const [deletingMenuId, setDeletingMenuId] = useState<string | null>(null);

  const handleDeleteClick = async (item: SetMenu) => {
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#590B21",
      cancelButtonColor: "#ddd",
      confirmButtonText: "Yes, delete it!",
    });

    if (confirmation.isConfirmed) {
      setDeletingMenuId(item.id);
      try {
        await deleteSetMenu({ restaurantId, setMenuId: item.id });
        Swal.fire("Deleted!", "Your menu has been deleted.", "success");
      } catch (error) {
        console.error("Delete Error:", error);
        Swal.fire(
          "Error!",
          "An error occurred while deleting the menu.",
          "error"
        );
      } finally {
        setDeletingMenuId(null);
      }
    }
  };

  const handleToggleChange = useCallback(
    async (menuId: string, status: boolean) => {
      try {
        await updateAvailabilityStatus({
          restaurantId,
          menuId,
          menuType: "SetMenu",
          status,
        }).unwrap();
      } catch (error) {
        console.error("Error updating availability status:", error);
        toast.error("Failed to update availability status.");
      }
    },
    [updateAvailabilityStatus, restaurantId]
  );

  return { handleDeleteClick, handleToggleChange, deletingMenuId };
};

const MenuTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Selecting restaurant details from Redux store
  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );
  const restaurantStatus = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.status
  );
  const rowsPerPage = useSelector((state: any) => state.setMenu.rowsPerPage);

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredMenus, setFilteredMenus] = useState<SetMenu[]>([]);

  // Using custom hooks for fetching data and handling mutations
  const { setMenus, isLoading } = useFetchSetMenus(restaurantId);
  const { handleDeleteClick, handleToggleChange, deletingMenuId } = useSetMenuMutations(
    restaurantId
  );

  // Filter menus based on search term
  useEffect(() => {
    if (setMenus && setMenus.length > 0) {
      if (searchTerm.trim() === "") {
        setFilteredMenus(setMenus);
      } else {
        const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);
        const filtered = setMenus.filter((item: SetMenu) => {
          const searchableText = [
            item.displayName || "",
            (item as any).productId || "",
          ].join(" ").toLowerCase();
          return searchWords.every(word => searchableText.includes(word));
        });
        setFilteredMenus(filtered);
      }
    }
  }, [setMenus, searchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handler for adding a new set menu
  const handleAddNewSetMenu = () => {
    if (restaurantStatus === "Pending") {
      toast.warning(
        "Your restaurant is still pending approval. Please wait for approval before adding new menus."
      );
    } else {
      navigate("/productCatalogue/combo-menu/add-menu");
    }
  };

  // Handler for editing a set menu
  const handleEditClick = (item: SetMenu) => {
    navigate(`/productCatalogue/combo-menu/update-menu`, { state: { item } });
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    dispatch(setRowsPerPage(newRowsPerPage));
  };


  // Define table columns
  const columns: TableColumn<SetMenu>[] = [
    {
      name: <h6 className="fw-bold">Image</h6>,
      cell: (row: SetMenu) => (
        <img
          src={getImageUrlWithFallback(row.imageUrl, DEFAULT_IMAGE_URL, 64) || DEFAULT_IMAGE_URL}
          alt=""
          className="set-menu-image"
        />
      ),
    },
    {
      name: <h6 className="fw-bold">Name</h6>,
      selector: (row: SetMenu) => row.displayName,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Price (Rs.)</h6>,
      selector: (row: SetMenu) => row.price,
    },
    {
      name: <h6 className="fw-bold">Created Date</h6>,
      selector: (row: SetMenu) => formatFirestoreTimestamp(row.createdAt),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Availability</h6>,
      cell: (row: SetMenu) => (
        <ToggleSwitch
          initialChecked={row.isAvailable}
          menuId={row.id}
          onChange={handleToggleChange}
        />
      ),
    },
    {
      name: <h6 className="fw-bold">Actions</h6>,
      cell: (row: SetMenu) => (
        <div className="d-flex gap-2">
          <Button variant="light" onClick={() => handleEditClick(row)}>
            <FontAwesomeIcon icon={faEye} title="Edit" color="#FF9A62" />
          </Button>
          {deletingMenuId === row.id ? (
            <Button variant="light" disabled>
              <FontAwesomeIcon icon={faSpinner} spin />
            </Button>
          ) : (
            <Button variant="light" onClick={() => handleDeleteClick(row)}>
              <FontAwesomeIcon icon={faTrash} title="Delete" color="#F24E1E" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <div className="set-menu-container">
        <div className="d-flex justify-content-end w-100">
          <ButtonComponent titel="Add" onClick={handleAddNewSetMenu} />
        </div>
        <Card className="set-menu-card">
          <CardHeader className="set-menu-card-header">
            <h4>Combo Menu</h4>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <TableLoadingComponent numColumns={5}/>
            ) : (
              <>
                {/* Search Input */}
                <div className="pb-3">
                  <InputGroup>
                    <InputGroupText>
                      <FontAwesomeIcon icon={faSearch} />
                    </InputGroupText>
                    <Input
                      type="text"
                      placeholder="Search combo menus by name or SKU ID..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="search-input"
                    />
                  </InputGroup>
                </div>
                <DataTable
                  columns={columns}
                  data={filteredMenus}
                  pagination
                  highlightOnHover
                  paginationPerPage={rowsPerPage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  noDataComponent={
                    <div className="py-4 text-center">
                      {searchTerm ? `No menus found for "${searchTerm}"` : "No menus available"}
                    </div>
                  }
                />
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
};

export default MenuTable;
